/**
 * usePayrollSettings - 급여 관리 설정 상태 관리 Hook
 *
 * payroll-management.tsx의 설정 관련 상태를 분리
 * Phase 2 Step 4: 설정 상태 Hook 분리
 *
 * @description
 * - 저성과 공제율 설정
 * - 연장 근무 배수 적용 여부
 * - 이전 월 조정 적용 여부
 * - 팀별 조정 적용 추적
 * - 수동 토글 타임스탬프
 */

import { useState, useCallback } from 'react';

// Settings State Types
export interface PayrollSettingsState {
  /** 저성과 공제율 (기본: 50%) */
  underperformanceDeductionRate: number;
  /** 연장 근무 배수 적용 여부 */
  applyOvertimeMultiplier: boolean;
  /** 이전 월 조정 적용 여부 */
  applyPreviousMonthAdjustment: boolean;
  /** 조정이 적용된 팀 Set */
  adjustmentAppliedTeams: Set<string>;
  /** 수동 토글 타임스탬프 (강제 재계산용) */
  manualToggleTimestamp: number;
}

export interface PayrollSettingsActions {
  // 저성과 공제율
  setUnderperformanceDeductionRate: (rate: number) => void;
  updateDeductionRate: (rate: number) => void;
  resetDeductionRate: () => void;

  // 연장 근무 배수
  setApplyOvertimeMultiplier: (apply: boolean) => void;
  toggleOvertimeMultiplier: () => void;
  enableOvertimeMultiplier: () => void;
  disableOvertimeMultiplier: () => void;

  // 이전 월 조정
  setApplyPreviousMonthAdjustment: (apply: boolean) => void;
  togglePreviousMonthAdjustment: () => void;
  enablePreviousMonthAdjustment: () => void;
  disablePreviousMonthAdjustment: () => void;

  // 조정 적용된 팀
  setAdjustmentAppliedTeams: React.Dispatch<React.SetStateAction<Set<string>>>;
  addAdjustmentAppliedTeam: (teamName: string) => void;
  removeAdjustmentAppliedTeam: (teamName: string) => void;
  isTeamAdjustmentApplied: (teamName: string) => boolean;
  clearAdjustmentAppliedTeams: () => void;

  // 수동 토글 타임스탬프
  setManualToggleTimestamp: (timestamp: number) => void;
  triggerManualToggle: () => void;

  // 유틸리티
  resetAllSettings: () => void;
}

export type UsePayrollSettingsReturn = PayrollSettingsState & PayrollSettingsActions;

// 기본값 상수
const DEFAULT_DEDUCTION_RATE = 50;

/**
 * 급여 관리 설정 상태 Hook
 */
export function usePayrollSettings(): UsePayrollSettingsReturn {
  // === State Declarations ===

  // 저성과 공제율 (기본: 50%)
  const [underperformanceDeductionRate, setUnderperformanceDeductionRate] = useState<number>(DEFAULT_DEDUCTION_RATE);

  // 연장 근무 배수 적용 여부
  const [applyOvertimeMultiplier, setApplyOvertimeMultiplier] = useState<boolean>(false);

  // 이전 월 조정 적용 여부
  const [applyPreviousMonthAdjustment, setApplyPreviousMonthAdjustment] = useState<boolean>(false);

  // 조정이 적용된 팀 Set
  const [adjustmentAppliedTeams, setAdjustmentAppliedTeams] = useState<Set<string>>(new Set());

  // 수동 토글 타임스탬프
  const [manualToggleTimestamp, setManualToggleTimestamp] = useState<number>(0);

  // === Action Implementations ===

  // 공제율 업데이트 (유효성 검사 포함)
  const updateDeductionRate = useCallback((rate: number) => {
    const validRate = Math.max(0, Math.min(100, rate));
    setUnderperformanceDeductionRate(validRate);
  }, []);

  // 공제율 기본값으로 리셋
  const resetDeductionRate = useCallback(() => {
    setUnderperformanceDeductionRate(DEFAULT_DEDUCTION_RATE);
  }, []);

  // 연장 근무 배수 토글
  const toggleOvertimeMultiplier = useCallback(() => {
    setApplyOvertimeMultiplier(prev => !prev);
  }, []);

  // 연장 근무 배수 활성화
  const enableOvertimeMultiplier = useCallback(() => {
    setApplyOvertimeMultiplier(true);
  }, []);

  // 연장 근무 배수 비활성화
  const disableOvertimeMultiplier = useCallback(() => {
    setApplyOvertimeMultiplier(false);
  }, []);

  // 이전 월 조정 토글
  const togglePreviousMonthAdjustment = useCallback(() => {
    setApplyPreviousMonthAdjustment(prev => !prev);
  }, []);

  // 이전 월 조정 활성화
  const enablePreviousMonthAdjustment = useCallback(() => {
    setApplyPreviousMonthAdjustment(true);
  }, []);

  // 이전 월 조정 비활성화
  const disablePreviousMonthAdjustment = useCallback(() => {
    setApplyPreviousMonthAdjustment(false);
  }, []);

  // 조정 적용 팀 추가
  const addAdjustmentAppliedTeam = useCallback((teamName: string) => {
    setAdjustmentAppliedTeams(prev => new Set([...prev, teamName]));
  }, []);

  // 조정 적용 팀 제거
  const removeAdjustmentAppliedTeam = useCallback((teamName: string) => {
    setAdjustmentAppliedTeams(prev => {
      const newSet = new Set(prev);
      newSet.delete(teamName);
      return newSet;
    });
  }, []);

  // 팀 조정 적용 여부 확인
  const isTeamAdjustmentApplied = useCallback((teamName: string): boolean => {
    return adjustmentAppliedTeams.has(teamName);
  }, [adjustmentAppliedTeams]);

  // 조정 적용 팀 전체 초기화
  const clearAdjustmentAppliedTeams = useCallback(() => {
    setAdjustmentAppliedTeams(new Set());
  }, []);

  // 수동 토글 트리거 (현재 타임스탬프로 업데이트)
  const triggerManualToggle = useCallback(() => {
    setManualToggleTimestamp(Date.now());
  }, []);

  // 모든 설정 초기화
  const resetAllSettings = useCallback(() => {
    setUnderperformanceDeductionRate(DEFAULT_DEDUCTION_RATE);
    setApplyOvertimeMultiplier(false);
    setApplyPreviousMonthAdjustment(false);
    setAdjustmentAppliedTeams(new Set());
    setManualToggleTimestamp(0);
  }, []);

  // === Return ===
  return {
    // State
    underperformanceDeductionRate,
    applyOvertimeMultiplier,
    applyPreviousMonthAdjustment,
    adjustmentAppliedTeams,
    manualToggleTimestamp,

    // Actions - 공제율
    setUnderperformanceDeductionRate,
    updateDeductionRate,
    resetDeductionRate,

    // Actions - 연장 근무 배수
    setApplyOvertimeMultiplier,
    toggleOvertimeMultiplier,
    enableOvertimeMultiplier,
    disableOvertimeMultiplier,

    // Actions - 이전 월 조정
    setApplyPreviousMonthAdjustment,
    togglePreviousMonthAdjustment,
    enablePreviousMonthAdjustment,
    disablePreviousMonthAdjustment,

    // Actions - 조정 적용 팀
    setAdjustmentAppliedTeams,
    addAdjustmentAppliedTeam,
    removeAdjustmentAppliedTeam,
    isTeamAdjustmentApplied,
    clearAdjustmentAppliedTeams,

    // Actions - 수동 토글
    setManualToggleTimestamp,
    triggerManualToggle,

    // Utilities
    resetAllSettings,
  };
}

export default usePayrollSettings;
